/* eslint-disable @typescript-eslint/prefer-promise-reject-errors */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingPanelService, LoadingPanelSettings } from '../../shared/loading-panel/loading-panel.service';
import { TrackingService } from './tracking.service';
import { Document } from '../models/document';
import { GenericModalService, ModalSettingsTemplate } from '../../shared/generic-modal/generic-modal.service';
import { TrackingResendModalComponent, TrackingResendModalResult } from '../tracking-resend-modal.component';
import { StakeholderService } from 'src/app/stakeholder/services/stakeholder.service';
import { ResendResult } from '../models';
import { InitConfiguration } from '../../distribution/models/init-configuration';
import { InitConfigurationDialogComponent } from '../../distribution/init-configuration-dialog/init-configuration-dialog.component';
import { UploadService } from '../../shared/upload/upload.service';
import { JsonHttpService } from 'src/app/shared/http/json-http.service';
import { TrackingChangeStakeholderModalComponent, TrackingChangeStakeholderModalResult } from '../tracking-change-stakeholder-modal/tracking-change-stakeholder-modal.component';

@Injectable({
    providedIn: 'root'
})
export class TrackingSharedActionMethodsService {
    private router = inject(Router);
    private jsonHttpService = inject(JsonHttpService);
    private fileUploadService = inject(UploadService);
    private loadingPanelService = inject(LoadingPanelService);
    private modalService = inject(GenericModalService);
    private stakeholderService = inject(StakeholderService);
    private trackingService = inject(TrackingService);

    public async resend(resendDocuments: ResendDocument[], selectedRows: number): Promise<ResendResult> {
        if (resendDocuments.length === 0) {
            throw new Error('No documents to resend');
        }

        const result: TrackingResendModalResult = await this.modalService.openComponent(TrackingResendModalComponent, { resendDocuments, selectedRows });

        this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Resending Documents'));

        try {
            const resendResult = await this.trackingService.resendDocuments(
                resendDocuments,
                result.comment,
                result.resendByInterchange,
                result.resendGenerateNewTransmissionId
            );

            this.loadingPanelService.hideLoadingPanel();

            await this.modalService.messageDirect(
                        'Document(s) Resent',
                        'The document(s) were resent successfully.'
                    );

            return resendResult;
        } catch (error) {
            this.loadingPanelService.hideLoadingPanel();
            return Promise.reject(null);
        }
    }

    public async handleManually(documentIds: number[], interchangeInIds: number[]): Promise<void> {
        try {
            if ((documentIds && (documentIds.length > 0)) || interchangeInIds && (interchangeInIds.length > 0)) {
                const comment = await this.modalService.inputDirect('Handle manually',
                    'Are you sure you want to set the selected document(s) as manually handled?<br /><br />Please enter a comment:', undefined, undefined, true);
                try {
                    this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Setting status on document(s)'));
                    const result = await this.trackingService.manuallyHandleDocuments(documentIds, interchangeInIds, comment);
                    this.loadingPanelService.hideLoadingPanel();

                    if (result.InterchangeIsProcessed) {
                        await this.modalService.messageDirect('Manually handled failed', 'This interchange could not be manually handled because it has already been processed.');
                    } else {
                        let message = 'The document(s) have now been set to Ignored';
                        if (result.RemovedFromInboxQueue > 0) {
                            message += '<br /><br />' + result.RemovedFromInboxQueue + ' document(s) have been removed from the Inbox queue.';
                        }
                        await this.modalService.messageDirect('Manually handled completed', message);
                    }
                }
                catch (error) {
                    console.error(error);
                    this.loadingPanelService.hideLoadingPanel();
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    public async setDocumentsOnHold(documentIds: number[], interchangeInIds: number[]): Promise<void> {
        try {
            if ((documentIds && (documentIds.length > 0)) || interchangeInIds && (interchangeInIds.length > 0)) {
                const comment = await this.modalService.inputDirect('On Hold',
                    'Are you sure you want to set the selected document(s) on hold?' +
                    '<br /><br />Please enter a comment:', undefined, undefined, true);
                try {
                    this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Setting status on document(s)'));
                    const result = await this.trackingService.setDocumentsOnHold(documentIds, interchangeInIds, comment);
                    this.loadingPanelService.hideLoadingPanel();

                    if (result.InterchangeIsProcessed) {
                        await this.modalService.messageDirect('On Hold failed', 'This interchange could not be put on hold because it has already been processed.');
                    } else {
                        let message = 'The document(s) have now been set to On Hold';
                        if (result && result.RemovedFromInboxQueue > 0) {
                            message += `<br/><br/>${result.RemovedFromInboxQueue} document(s) have been removed from the Inbox queue.`;
                        }
                        await this.modalService.messageDirect('On Hold completed', message);
                    }
                }
                catch (error) {
                    console.error(error);
                    this.loadingPanelService.hideLoadingPanel();
                }
            }
        }
        catch (error) {
            console.error(error);
        }
    }

    public delete(documentIds: number[], interchangeInIds: number[]): Promise<void> {
        if ((documentIds && (documentIds.length > 0)) || interchangeInIds && (interchangeInIds.length > 0)) {
            return this.modalService.confirmDirect('Delete', 'Are you sure you want to delete the selected document(s)?').then(() => {
                this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Deleting documents'));
                return this.trackingService.deleteDocuments(documentIds, interchangeInIds).then(() => {
                    this.loadingPanelService.hideLoadingPanel();
                    return this.modalService.messageDirect('Document(s) deleted', 'The document(s) have now been deleted');
                }).catch(() => { this.loadingPanelService.hideLoadingPanel(); return Promise.reject(null); });
            }).catch(() => { this.loadingPanelService.hideLoadingPanel(); return Promise.reject(null); });
        }
        return Promise.reject(null);
    }

    public async changeStakeholder(documentIds: number[], interchangeInIds: number[], selectedStakeHolderId?: number): Promise<void> {
        try {
            const stakeholders = await this.stakeholderService.getStakeholders();
            const modalSettings = new ModalSettingsTemplate(undefined, 'static');
            const result = await this.modalService.openComponentWithInitialize<TrackingChangeStakeholderModalResult, TrackingChangeStakeholderModalComponent>(
                TrackingChangeStakeholderModalComponent, modalSettings, dialog => {
                    dialog.stakeholders.set(stakeholders);
                    dialog.selectedStakeholderId.set(selectedStakeHolderId);
                });

            try {
                this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Changing stakeholder...'));
                const response = await this.trackingService.changeStakeholder(documentIds, interchangeInIds, result.stakeholderId, result.comment);

                if (response && response.status === 200 && response.body?.IsEmailSendt === false) {
                    const message = response.body?.Response || response.Response;
                    this.loadingPanelService.hideLoadingPanel();
                    await this.modalService.messageDirect('Change stakeholder', message as string);
                }
            }
            finally {
                this.loadingPanelService.hideLoadingPanel();
            }
        }
        catch {
            throw new Error('Failed to change stakeholder.');
        }
    }

    public recreate(documentIds: number[], interchangeInIds: number[]): Promise<void> {
        this.loadingPanelService.showLoadingPanel(new LoadingPanelSettings('Checking documents for re-create'));
        void this.trackingService.reCreateCheckDocuments(documentIds, interchangeInIds).then(checkResult => {
            this.loadingPanelService.hideLoadingPanel();

            const unSupportedDocumentIds = checkResult.UnSupportedDocuments as number[];
            const supportedDocumentIds = checkResult.SupportedDocuments as number[];

            const documentNotReadyLines = unSupportedDocumentIds.join(', ');
            const documentReadyLines = supportedDocumentIds.join(', ');

            const messageNotReady = 'The following documents are <b>not</b> available for re-create ' +
                '(either missing Originating System or missing ReCreate status point). ' +
                `Document id's: <br /><pre>${documentNotReadyLines}</pre>`;
            const messageReady = `The following documents are ready to be re-created. Document id's: <br /><pre>${documentReadyLines}</pre>`;

            if (unSupportedDocumentIds.length > 0 && supportedDocumentIds.length > 0) {

                const message = `${messageNotReady} <br> ${messageReady} <br>Continue with available documents?`;
                this.modalService.confirmDirect('Missing status point "ReCreate"', message).then(() => {
                    this.recreateDocuments(supportedDocumentIds, interchangeInIds);
                }).catch(() => { });
            }
            else if (supportedDocumentIds.length > 0) {
                this.modalService.confirmDirect('Ready for re-create', `${messageReady}<br>Continue with re-create?`).then(() => {
                    this.recreateDocuments(supportedDocumentIds, interchangeInIds);
                }).catch(() => { });
            }
            else {
                this.modalService.messageDirect('Missing status point "ReCreate"', messageNotReady).then(() => { }).catch(() => { });
            }
        });

        return Promise.resolve();
    }

    recreateDocuments(documentIds: number[], interchangeInIds: number[]) {
        this.modalService.inputDirect('Comment', 'Please enter a comment').then(comment => {
            this.loadingPanelService.showLoadingPanelDirect('Re-creating documents...');
            void this.trackingService.reCreateDocuments(documentIds, interchangeInIds, comment || '').then(() => {
                this.loadingPanelService.hideLoadingPanel();
                void this.modalService.messageDirect('Recreate done', 'Re-create successfully performed.');
            });
        }).catch(() => { });
    }

    createDocumentLine(document: Document) {
        const documentNumber = document.DocumentNumber || 'unknown';
        const fromPartnerName = document.PartnerIn ? document.PartnerIn.PartnerName : 'Unknown';
        const toPartnerName = document.PartnerOut ? document.PartnerOut.PartnerName : 'Unknown';
        const documentString = `(${documentNumber}) from ${fromPartnerName} to ${toPartnerName}`;
        return documentString;
    }

    async uploadDocument(headline: string, initConfiguration?: InitConfiguration): Promise<void> {
        let initConfig = initConfiguration;
        if (!initConfig) {
            initConfig = {} as InitConfiguration;
        }
        const componentValues = {
            initConfiguration: initConfig,
            headline: headline
        };

        let files: File[] = [];
        try {
            const maxUploadSize: number = await this.jsonHttpService.get('api/upload/get-max-file-size').toPromise();
            files = await this.modalService.fileSelectorDirect('Upload file', 'Select files to upload',
                true, 'Upload', undefined, undefined, 100, maxUploadSize);
        }
        catch {
            // User dismissed the dialog
            return;
        }

        const fileGuids = await this.fileUploadService.uploadFiles(files);

        const modalSettings = new ModalSettingsTemplate('lg', 'static');
        const result = await this.modalService.openComponentType<InitConfiguration>(InitConfigurationDialogComponent, componentValues, modalSettings);
        const uploadResponse = await this.trackingService.uploadDocuments(fileGuids, result);

        // Ask the user if we should navigate to the created document
        if (uploadResponse.DocumentResults.length > 0) {
            try {
                await this.modalService.confirmDirect('Upload successful',
                    `<pre>The files (count: ${uploadResponse.DocumentResults.length})\r\n\r\n${uploadResponse.DocumentResults.map(u => u.Filename)
                        .join('\r\n')}\r\n\r\nHave been successfully uploaded. Do you want to go to interchange details?</pre>`, 'Yes', 'No');
                void this.router.navigate(['tracking/result/details', { interchangeInIds: uploadResponse.DocumentResults.map(u => u.InterchangeId) }]);
            } catch { /* Ignore */ }
        }
    }
}

export interface ResendDocument {
    interchangeInId: number;
    documentId?: number;
}
